import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  House, Users, Buildings, Handshake, Package, ChartBar,
  MagnifyingGlass, UserCircle, SignOut, List, X, FunnelSimple,
  CurrencyEur, TreeStructure, Lightbulb, ChartLineUp, Gear, Target,
  UsersFour, UserCheck, BellRinging, Wrench, Shield, ChartPieSlice,
  GraduationCap, Trophy, Wallet,
} from '@phosphor-icons/react';

const navItems = [
  { to: '/', label: 'Dashboard', icon: House },
  { to: '/alertas', label: 'Alertas', icon: BellRinging },
  { to: '/leads', label: 'Leads', icon: FunnelSimple },
  { to: '/negocios', label: 'Negócios', icon: Handshake },
  { to: '/encomendas', label: 'Encomendas', icon: Package },
  { to: '/comissoes', label: 'Comissões', icon: CurrencyEur },
  { to: '/rede', label: 'Rede', icon: TreeStructure },
  { to: '/rede/saldos', label: 'Saldos da Rede', icon: Wallet },
  { to: '/cursos/inscricoes', label: 'Cursos da rede', icon: GraduationCap },
  { to: '/empresas', label: 'Empresas', icon: Buildings },
  { to: '/oportunidades', label: 'Oportunidades', icon: Target },
  { to: '/indicadores', label: 'Indicadores', icon: ChartBar },
  { to: '/incentivos', label: 'Incentivos', icon: Trophy },
  { to: '/comparativos', label: 'Comparativos', icon: ChartLineUp },
  { to: '/objetivos', label: 'Objetivos', icon: Target },
  { to: '/pesquisa', label: 'Pesquisa', icon: MagnifyingGlass },
  { to: '/ferramentas', label: 'Ferramentas', icon: Wrench },
];

const bottomItems = [
  { to: '/backoffice', label: 'BackOffice', icon: Shield, adminOnly: true },
  { to: '/backoffice/analytics', label: 'Atividade', icon: ChartPieSlice, adminOnly: true },
  { to: '/config', label: 'Config APIs', icon: Gear, adminOnly: true },
  { to: '/perfil', label: 'Perfil', icon: UserCircle },
  { to: '/notificacoes', label: 'Notificações', icon: BellRinging },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.ID_Cliente === 1;
  const visibleBottomItems = isAdmin ? bottomItems : bottomItems.filter(i => !i.adminOnly);

  // Close the mobile sidebar when a nav link is clicked (below lg breakpoint)
  const handleNavClick = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024 && !collapsed) {
      onToggle();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {!collapsed && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={onToggle} />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 lg:left-[72px] z-50 h-full bg-white border-r border-[#E5E7EB] flex flex-col transition-all duration-200
          ${collapsed ? '-translate-x-full lg:translate-x-0 lg:w-[72px]' : 'translate-x-0 w-[260px]'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[#E5E7EB]">
          {!collapsed && (
            <span className="font-black text-lg tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
              IHTP CRM
            </span>
          )}
          <button onClick={onToggle} className="p-1.5 rounded-md hover:bg-[#F3F4F6] transition-colors" data-testid="sidebar-toggle">
            {collapsed ? <List size={20} /> : <X size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 overflow-y-auto">
          <div className="px-3 space-y-0.5">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={handleNavClick}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150
                  ${isActive
                    ? 'bg-[#0F1829] text-white'
                    : 'text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827]'
                  }
                  ${collapsed ? 'justify-center' : ''}`
                }
              >
                <item.icon size={20} weight="duotone" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Bottom */}
        <div className="border-t border-[#E5E7EB] py-3 px-3 space-y-0.5">
          {visibleBottomItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150
                ${isActive ? 'bg-[#0F1829] text-white' : 'text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827]'}
                ${collapsed ? 'justify-center' : ''}`
              }
            >
              <item.icon size={20} weight="duotone" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
          <button
            onClick={logout}
            data-testid="logout-btn"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#FF2B2B] hover:bg-red-50 w-full transition-all duration-150
              ${collapsed ? 'justify-center' : ''}`}
          >
            <SignOut size={20} weight="duotone" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
